import { doc, setDoc, getDoc, updateDoc, deleteField } from "firebase/firestore";
import { db } from "@/firebase";
import { getBaseIngredients } from "@/service/base_ingredient_service";
import { getHiddenBaseIngredients } from "@/service/hidden_base_ingredient_service";

const buildShoppingListDocId = (userId) => `${userId}-shopping_list`;

const normalizeListName = (name) => {
  const base = String(name || "liste")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "liste";
};

export const createShoppingListId = (name, now = Date.now()) =>
  `${now}-${normalizeListName(name)}`;

/**
 * Récupère toutes les listes d'un utilisateur.
 * Stockage: collection "ShoppingLists", doc "${userId}-shopping_list"
 * Retourne une map { [listId]: list }
 */
export async function getUserShoppingLists(userId) {
  if (!userId) return {};
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return {};
    return snap.data() || {};
  } catch (e) {
    console.error("❌ Erreur getUserShoppingLists:", e);
    throw e;
  }
}

/**
 * Ajoute une liste pour un utilisateur.
 * listData doit contenir au minimum { name, items }
 * Retourne { id }
 */
export async function addShoppingList(userId, listData = {}) {
  if (!userId) throw new Error("userId manquant");
  const listName = listData.name || "Liste";
  const listId = createShoppingListId(listName);
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));

  const payload = {
    ...listData,
    id: listId,
    name: listName,
    items: Array.isArray(listData.items) ? listData.items : [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  try {
    await setDoc(docRef, { [listId]: payload }, { merge: true });
    return { id: listId };
  } catch (e) {
    console.error("❌ Erreur addShoppingList:", e);
    throw e;
  }
}

/**
 * Met à jour une liste existante (ou la crée si absente).
 * listData écrase la liste au complet pour listId.
 */
export async function updateShoppingList(userId, listId, listData = {}) {
  if (!userId || !listId) throw new Error("userId ou listId manquant");
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  let createdAt = listData.createdAt;
  if (!createdAt) {
    try {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const existing = snap.data()?.[listId];
        createdAt = existing?.createdAt;
      }
    } catch (e) {
      console.warn("⚠️ Impossible de récupérer createdAt existant:", e);
    }
  }

  const payload = {
    ...listData,
    id: listId,
    createdAt: createdAt ?? Date.now(),
    updatedAt: Date.now(),
  };
  try {
    await setDoc(docRef, { [listId]: payload }, { merge: true });
    return { id: listId };
  } catch (e) {
    console.error("❌ Erreur updateShoppingList:", e);
    throw e;
  }
}

/**
 * Supprime une liste d'un utilisateur.
 */
export async function deleteShoppingList(userId, listId) {
  if (!userId || !listId) throw new Error("userId ou listId manquant");
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  try {
    await updateDoc(docRef, { [listId]: deleteField() });
    return { deleted: true };
  } catch (e) {
    if (e.code === "not-found" || /No document to update/.test(String(e))) {
      return { deleted: false };
    }
    console.error("❌ Erreur deleteShoppingList:", e);
    throw e;
  }
}

/**
 * Synchronise la liste de courses générée depuis le menu hebdo.
 * - Si une liste `generatedFromMenu` existe déjà, elle est remplacée.
 * - Si aucune liste n'existe, une nouvelle est créée.
 * - Si le menu est vide / sans ingrédients, rien n'est fait.
 * Retourne { id, items } ou null.
 */
export async function syncMenuShoppingList(userId, weeklyMenu) {
  if (!userId || !weeklyMenu?.daily) return null;

  // Agréger tous les ingrédients du menu
  const ingredientsMap = {};
  for (const day of weeklyMenu.daily) {
    if (!day.menu) continue;
    for (const meals of Object.values(day.menu)) {
      for (const recipe of meals) {
        if (!recipe.ingredients) continue;
        for (const ing of recipe.ingredients) {
          const name = ing.name || ing.productName || '';
          if (!name) continue;
          const key = name.toLowerCase().trim();
          if (ingredientsMap[key]) {
            ingredientsMap[key].grams = (ingredientsMap[key].grams || 0) + (ing.grams || 0);
          } else {
            ingredientsMap[key] = { id: ing.id || null, name, grams: ing.grams || 0, quantity: '1', checked: false };
          }
        }
      }
    }
  }

  // Enrichir les items avec les images depuis les collections de base
  try {
    const [baseData, hiddenData] = await Promise.all([
      getBaseIngredients(),
      getHiddenBaseIngredients(),
    ]);
    const allIngredients = { ...hiddenData, ...baseData };
    for (const item of Object.values(ingredientsMap)) {
      const match = allIngredients[item.id];
      if (match?.productImage) {
        item.productImage = match.productImage;
      }
    }
  } catch (e) {
    console.warn('Impossible de récupérer les images des ingrédients:', e);
  }

  const items = Object.values(ingredientsMap);
  if (!items.length) return null;

  const listName = 'Semaine du ' + new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

  // Chercher si une liste générée depuis le menu existe déjà
  const existing = await getUserShoppingLists(userId);
  const existingEntry = Object.entries(existing).find(([, l]) => l.generatedFromMenu);

  if (existingEntry) {
    const [existingId, existingList] = existingEntry;
    await updateShoppingList(userId, existingId, {
      ...existingList,
      name: listName,
      items,
      generatedFromMenu: true,
      updatedAt: Date.now(),
    });
    return { id: existingId, items };
  } else {
    const { id } = await addShoppingList(userId, { name: listName, items, generatedFromMenu: true });
    return { id, items };
  }
}

/**
 * Récupère une liste par son id.
 * Retourne { id, ...list } ou null.
 */
export async function getUserShoppingListById(userId, listId) {
  if (!userId || !listId) return null;
  const docRef = doc(db, "ShoppingLists", buildShoppingListDocId(userId));
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    const list = data[listId] || null;
    return list ? { id: listId, ...list } : null;
  } catch (e) {
    console.error("❌ Erreur getUserShoppingListById:", e);
    throw e;
  }
}
